import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText,
  History,
  MessageCircle,
  MessageSquare,
  Send,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronDown,
  X,
  ArrowLeft,
} from 'lucide-react';
import { useDraftCollaboration } from '../../hooks/useDraftCollaboration';
import RichTextEditor from '../editor/RichTextEditor';
import ExportFormatModal from './ExportFormatModal';
import VersionComparison from './VersionComparison';
import { downloadBlob, convertContent, createWordDocumentHtml } from '../../utils/documentExport';
import { VisibilityBadge, VisibilityToggle } from './CommentVisibility';
import { useQuery } from '@tanstack/react-query';
import { documentsAPI } from '../../services/api';

/**
 * ClientDraftReview Component
 * Client interface for reviewing, providing feedback, and approving drafts
 */
export default function ClientDraftReview({
  documentId,
  preferredMode,
  initialOfflineFile,
  onClose,
}) {
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const apiRoot = apiBase.replace(/\/api\/?$/, '');
  const resolveFileUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/uploads') || url.startsWith('/api')) {
      return `${apiRoot}${url}`;
    }
    return url;
  };
  const {
    document,
    isLoading,
    error,
    submitClientDraft,
    approveDraft,
    requestChanges,
    getDraftHistory,
    updateWorkflow,
    advanceWorkflow,
    returnWorkflow,
    saveContent,
  } = useDraftCollaboration(documentId);

  // One comment thread per document, shared with Stratel's editor; the server filters out Stratel's internal notes
  const { data: threadComments = [], refetch: refetchComments } = useQuery({
    queryKey: ['document-comments', documentId],
    queryFn: () => documentsAPI.getComments(documentId),
    select: (res) => res.data?.comments || [],
    enabled: !!documentId,
  });

  const [activeTab, setActiveTab] = useState('current');
  const [selectedFile, setSelectedFile] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedIteration, setExpandedIteration] = useState(null);
  const [history, setHistory] = useState(null);
  const [hasApproved, setHasApproved] = useState(false);
  const [mode, setMode] = useState('in_app');
  const [returnIndex, setReturnIndex] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState(null); // 'download' or 'diff'
  const [isExporting, setIsExporting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [approveError, setApproveError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentVisibility, setCommentVisibility] = useState('internal');
  const [returnReason, setReturnReason] = useState('');
  const [returnError, setReturnError] = useState('');
  const [isCommentSaving, setIsCommentSaving] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const workflow = document?.collaborationWorkflow || {};
  const clientSteps = workflow.client?.steps || [];
  const activeStepIndex = workflow.client?.activeStep || 0;
  const activeStep = clientSteps[activeStepIndex];

  const currentIteration = useMemo(() => {
    if (!document?.draftIterations?.length) return null;
    return document.draftIterations[document.draftIterations.length - 1];
  }, [document]);

  const originalIteration = useMemo(() => {
    if (!document?.draftIterations?.length) return null;
    const sharedIterationNumber = document.draftSharedWithClientIteration;
    if (sharedIterationNumber) {
      return document.draftIterations.find(
        (iter) => iter.iterationNumber === sharedIterationNumber
      );
    }
    return document.draftIterations[0] || null;
  }, [document]);

  useEffect(() => {
    setMode(workflow?.modeByTeam?.client || 'in_app');
  }, [workflow?.modeByTeam?.client]);

  useEffect(() => {
    setEditorContent(document?.content || '');
  }, [document?.content]);

  const baselineContent = document?.draftSharedWithClientContent || document?.content || '';
  const hasInAppEdits = mode === 'in_app' && editorContent.trim() !== baselineContent.trim();
  const hasOfflineEdits = mode === 'offline' && !!selectedFile;
  const sharedIterationNumber = document?.draftSharedWithClientIteration || 0;
  const hasClientIterations = (document?.draftIterations || []).some(
    (iter) => iter.uploadedByRole === 'client' && iter.iterationNumber > sharedIterationNumber
  );
  const hasClientEdits = hasInAppEdits || hasOfflineEdits || hasClientIterations;

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmitDraft = async () => {
    if (!selectedFile) return;
    setIsSubmitting(true);
    try {
      await submitClientDraft(selectedFile, { content: feedback });
      setSelectedFile(null);
      setFeedback('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveDraft = async () => {
    if (hasClientEdits) {
      setApproveError('You\'ve made edits. Please request changes instead of approving.');
      return;
    }
    setApproveError('');
    await approveDraft('client');
    setHasApproved(true);
  };

  const handleRequestChanges = async () => {
    if (!feedback.trim()) {
      setFeedbackError('Please add feedback before requesting changes.');
      return;
    }
    setFeedbackError('');
    setApproveError('');
    setIsSubmitting(true);
    try {
      await requestChanges(feedback.trim() || 'Requested revisions from client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeChange = async (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    await updateWorkflow({ modeByTeam: { client: nextMode } });
  };

  useEffect(() => {
    if (!preferredMode || !document) return;
    if (preferredMode === mode) return;
    void handleModeChange(preferredMode);
  }, [preferredMode, document, mode]);

  useEffect(() => {
    if (!initialOfflineFile) return;
    setSelectedFile(initialOfflineFile);
  }, [initialOfflineFile]);

  const isMidRouting = clientSteps.length > 0 && activeStepIndex < clientSteps.length - 1;
  const isLastReviewer = !isMidRouting; // either no workflow steps, or currently on the final step

  const handleSaveDraftContent = async () => {
    setIsSaving(true);
    try {
      const cleanName = (document?.name || 'client-draft').replace(/\.[^/.]+$/, '');
      await saveContent(createWordDocumentHtml(editorContent), `${cleanName}.doc`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdvance = async () => {
    // Auto-save in-app edits so the next reviewer sees the changes
    if (hasInAppEdits) {
      await handleSaveDraftContent();
    }
    await advanceWorkflow({ team: 'client', stepIndex: activeStepIndex });
  };

  const handleReturn = async () => {
    const index = parseInt(returnIndex, 10);
    if (Number.isNaN(index)) return;
    if (!returnReason.trim()) {
      setReturnError('Explain why you are returning it.');
      return;
    }
    setReturnError('');
    try {
      await returnWorkflow({ team: 'client', stepIndex: index, reason: returnReason.trim() });
      setReturnIndex('');
      setReturnReason('');
      refetchComments();
    } catch (error) {
      setReturnError(error.message || 'Failed to return the document');
    }
  };

  const handleLoadHistory = async () => {
    const hist = await getDraftHistory();
    setHistory(hist);
  };

  const handleOpenHistory = async () => {
    setShowHistoryModal(true);
    if (historyVersions.length || historyLoading) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res = await getDraftHistory();
      const iterations =
        res?.iterations ||
        res?.data?.iterations ||
        res?.history ||
        res?.data?.history ||
        res?.documents ||
        res || [];
      const list = Array.isArray(iterations) ? iterations : [];
      // Also keep raw history for the history tab
      if (res?.iterations || res?.data?.iterations) setHistory(res);
      const normalized = await Promise.all(
        list.map(async (item, idx) => {
          const versionNumber =
            item?.versionNumber || item?.iterationNumber || item?.version || idx + 1;
          const uploadedAt =
            item?.uploadedAt ||
            item?.createdAt ||
            item?.updatedAt ||
            new Date().toISOString();
          let body = item?.contentSnapshot || item?.content || item?.html || item?.text || '';
          if (!body && item?.fileUrl) {
            try {
              const resp = await fetch(resolveFileUrl(item.fileUrl));
              body = await resp.text();
            } catch (e) {
              // ignore fetch errors; still allow history entry
            }
          }
          return {
            _id: item?._id || item?.id || `${idx}`,
            versionNumber,
            uploadedAt,
            content: body || '',
          };
        })
      );
      setHistoryVersions(normalized);
    } catch (error) {
      setHistoryError(error.message || 'Failed to load draft history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDownloadDraft = (format) => {
    try {
      setIsExporting(true);
      const { blob, extension } = convertContent(editorContent, format);
      const cleanName = (document?.name || 'draft').replace(/\.[^/.]+$/, '');
      const fileName = `${cleanName}${extension}`;
      downloadBlob(blob, fileName);
      setShowExportModal(false);
      setExportType(null);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportDiff = (format) => {
    try {
      setIsExporting(true);
      const originalContent = document?.draftSharedWithClientContent || document?.content || '';
      const diffHtml = `
        <h2>Version Comparison</h2>
        <h3>Original</h3>
        ${originalContent}
        <h3>Current</h3>
        ${editorContent}
      `;
      const { blob, extension } = convertContent(diffHtml, format);
      const fileName = `draft-comparison${extension}`;
      downloadBlob(blob, fileName);
      setShowExportModal(false);
      setExportType(null);
    } catch (error) {
      console.error('Export diff failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSelect = (format) => {
    if (exportType === 'download') {
      handleDownloadDraft(format);
    } else if (exportType === 'diff') {
      handleExportDiff(format);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setIsCommentSaving(true);
    try {
      await documentsAPI.addComment(documentId, {
        content: commentText.trim(),
        visibility: commentVisibility,
      });
      setCommentText('');
      setCommentVisibility('internal');
      refetchComments();
    } finally {
      setIsCommentSaving(false);
    }
  };


  const getStatusColor = (status) => {
    const colors = {
      shared_with_client: 'bg-teal-100 text-teal-800',
      client_review: 'bg-emerald-100 text-emerald-800',
      awaiting_internal_response: 'bg-orange-100 text-orange-800',
      draft_approved: 'bg-green-100 text-green-800',
      final_version_ready: 'bg-cyan-100 text-cyan-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      shared_with_client: 'Ready for Your Review',
      client_review: 'Under Review',
      awaiting_internal_response: 'Response Sent',
      draft_approved: 'Approved',
      final_version_ready: 'Ready to Sign',
    };
    return labels[status] || status;
  };

  if (isLoading && !document) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading document...</div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-red-500">Document not found</div>
      </div>
    );
  }

  const isDocumentSharedWithClient = [
    'shared_with_client',
    'client_review',
    'awaiting_internal_response',
    'draft_approved',
  ].includes(document.status);

  // Always render as full-screen overlay when opened from the document page (onClose provided)
  const isInAppOverlay =
    !!onClose && ['shared_with_client', 'client_review'].includes(document.status);

  const plainText = (editorContent || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim();
  const wordCount = plainText ? plainText.split(/\s+/).length : 0;

  const headerLeft = (
    <div className="min-w-0 flex-1 pr-3">
      <h2 className="text-lg sm:text-2xl font-bold text-gray-900 break-all">{document.name}</h2>
      <p className="text-sm text-gray-600 mt-1">Draft Review & Approval</p>
    </div>
  );

  const headerRight = document.status && isDocumentSharedWithClient ? (
    <div
      className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
        document.status
      )}`}
    >
      {getStatusLabel(document.status)}
    </div>
  ) : null;

  const body = (
    <>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Document Not Shared Yet */}
      {!isDocumentSharedWithClient && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Awaiting Document</p>
            <p className="text-sm text-blue-700 mt-1">
              The internal team is still preparing the draft. You will be notified when it is
              ready for your review.
            </p>
          </div>
        </div>
      )}

      {isDocumentSharedWithClient && (
        <>
          {document.draftSharedWithClientNotes && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Notes from the team</p>
                <p className="text-sm text-blue-800 mt-1">{document.draftSharedWithClientNotes}</p>
              </div>
            </div>
          )}



          {/* Tabs */}
          {!isInAppOverlay && (
            <div className="border-b border-gray-200">
              <div className="flex gap-8">
                <button
                  onClick={() => setActiveTab('current')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'current'
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Current Draft
                </button>
                <button
                  onClick={() => {
                    setActiveTab('history');
                    if (!history) handleLoadHistory();
                  }}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'history'
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  History & Diff
                </button>
              </div>
            </div>
          )}

          {/* Current Draft Tab */}
          {activeTab === 'current' && currentIteration && (
            <div className="space-y-6">
              {/* Collaboration Mode toggle — only show on non-overlay page view */}
              {!isInAppOverlay && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">How do you want to edit this document?</h3>
                      <p className="text-sm text-gray-500 mt-0.5">Edit directly in the browser, or download, edit externally, and upload your changes.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleModeChange('in_app')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                          mode === 'in_app'
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        Edit in browser
                      </button>
                      <button
                        type="button"
                        onClick={() => handleModeChange('offline')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                          mode === 'offline'
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        Upload edited file
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {mode === 'in_app' && ['shared_with_client', 'client_review'].includes(document.status) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-gradient-to-r from-teal-50 to-teal-100">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-teal-600 text-white text-xs rounded-full font-medium">
                          CLIENT REVIEW
                        </span>
                        <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
                          {mode === 'offline' ? 'Offline' : 'In-app'}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">In-app Draft Editor</h3>
                      <p className="text-sm text-gray-600">
                        Review and annotate the draft before submitting feedback.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row min-h-[520px]">
                    <div
                      className={`flex-1 flex flex-col min-h-0 ${showComments ? 'md:border-r border-gray-200' : ''}`}
                    >
                      <div className="flex-1 flex flex-col min-h-0">
                        <RichTextEditor
                          value={editorContent}
                          onChange={setEditorContent}
                          placeholder="Start editing your document here..."
                          showVariables={false}
                          minHeight="360px"
                        />
                      </div>
                      <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
                        <div>{plainText.length} characters and {wordCount} words</div>
                        <div>{(currentIteration?.comments || []).length} comments</div>
                      </div>
                      <div className="border-t border-gray-200 bg-white p-4 space-y-3">
                        <div className="flex flex-wrap gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => setShowComments((prev) => !prev)}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <MessageCircle size={18} />
                            Comments ({(currentIteration?.comments || []).length})
                          </button>
                          <button
                            type="button"
                            onClick={handleOpenHistory}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <History size={18} />
                            History & Diff
                          </button>
                          <button
                            type="button"
                            onClick={() => { setExportType('download'); setShowExportModal(true); }}
                            disabled={isSaving || isExporting}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <Download size={18} />
                            Download Draft
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveDraftContent}
                            disabled={isSaving}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300"
                          >
                            <Send size={18} />
                            {isSaving ? 'Saving...' : 'Save Draft'}
                          </button>
                        </div>
                        <p className="text-sm text-gray-600">
                          Save your edits so the internal team can review the latest client changes.
                        </p>
                      </div>
                    </div>

                    {showComments && (
                      <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-gray-200 flex flex-col bg-white max-h-[70vh] md:max-h-none">
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                          {threadComments.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-8">No comments yet</p>
                          ) : (
                            threadComments.map((comment) => (
                              <div
                                key={comment._id}
                                className="p-3 rounded-lg border bg-yellow-50 border-yellow-200"
                              >
                                <div className="flex items-start justify-between mb-1">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {[comment.userId?.firstName, comment.userId?.lastName].filter(Boolean).join(' ') || (comment.team === 'internal' ? 'Stratel' : 'Your team')}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                      {new Date(comment.createdAt).toLocaleDateString()}
                                    </p>
                                    <div className="mt-1"><VisibilityBadge comment={comment} /></div>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-700">{comment.content}</p>
                              </div>
                            ))
                          )}
                        </div>

                        <div className="border-t border-gray-200 p-4 space-y-2">
                          <VisibilityToggle value={commentVisibility} onChange={setCommentVisibility} />
                          <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder={commentVisibility === 'shared' ? 'Comment Stratel will see...' : 'Internal note for your team...'}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                            rows={3}
                          />
                          <button
                            type="button"
                            onClick={handleAddComment}
                            disabled={isCommentSaving || !commentText.trim()}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:bg-gray-300"
                          >
                            <Send size={16} />
                            {isCommentSaving ? 'Adding...' : 'Comment'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {clientSteps.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Internal Review Routing</h3>
                    {isMidRouting ? (
                      <p className="text-sm text-gray-500 mt-0.5">
                        Review the draft, then pass it to the next team member. Only the final reviewer in your sequence can submit your team's response to Stratel.
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-0.5">
                        You are the final reviewer. When your team is ready, submit your response to Stratel below.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className={`px-2 py-1 rounded-full font-medium ${
                      isMidRouting ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'
                    }`}>
                      {isMidRouting
                        ? `Step ${activeStepIndex + 1} of ${clientSteps.length}: ${activeStep?.name || activeStep?.email || 'Unassigned'}`
                        : `Final reviewer: ${activeStep?.name || activeStep?.email || 'Unassigned'}`}
                    </span>
                    {activeStep?.dueDate && (
                      <span className="text-gray-500">Due {new Date(activeStep.dueDate).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {isMidRouting && (
                      <button
                        onClick={handleAdvance}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {isSubmitting ? 'Saving...' : 'Done — forward to next reviewer'}
                      </button>
                    )}
                    {clientSteps.length > 1 && (
                      <div className="flex items-center gap-2">
                        <select
                          value={returnIndex}
                          onChange={(e) => setReturnIndex(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
                        >
                          <option value="">Return to reviewer...</option>
                          {clientSteps.map((step, idx) => idx !== activeStepIndex && (
                            <option key={step._id || step.id || idx} value={idx}>
                              {step.name || step.email || `Reviewer ${idx + 1}`}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleReturn}
                          disabled={isSubmitting || !returnIndex || !returnReason.trim()}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 text-sm"
                        >
                          Return
                        </button>
                      </div>
                    )}
                  </div>
                  {returnIndex !== '' && (
                    <div className="space-y-1">
                      <textarea
                        value={returnReason}
                        onChange={(e) => setReturnReason(e.target.value)}
                        placeholder="Why are you returning it? (required, visible to your team only)"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        rows={2}
                      />
                      {returnError && <p className="text-xs text-red-600">{returnError}</p>}
                    </div>
                  )}
                  {isMidRouting && hasInAppEdits && (
                    <p className="text-xs text-amber-700 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      Your in-browser edits will be saved automatically when you forward to the next reviewer.
                    </p>
                  )}
                </div>
              )}

              {/* Submit Your Response — only visible to the final/sole reviewer */}
              {['shared_with_client', 'client_review'].includes(document.status) && isLastReviewer ? (
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Submit Your Response</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Once your team has finished reviewing, choose one of the options below to send your response to Stratel.
                    </p>
                  </div>

                  {/* Offline upload area */}
                  {mode === 'offline' && (
                    <div>
                      {selectedFile ? (
                        <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                          <FileText className="w-5 h-5 text-teal-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-teal-800 truncate">{selectedFile.name}</span>
                          <button type="button" onClick={() => setSelectedFile(null)} className="ml-auto text-teal-600 hover:text-teal-800 text-xs underline">Remove</button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-5 hover:border-gray-400 transition-colors">
                          <input type="file" accept=".pdf,.doc,.docx,.rtf,.txt" onChange={handleFileSelect} className="hidden" id="file-input" />
                          <label htmlFor="file-input" className="cursor-pointer block text-center">
                            <FileText className="w-7 h-7 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">Click to attach your edited file</p>
                            <p className="text-xs text-gray-500 mt-1">.pdf, .doc, .docx, .rtf, .txt</p>
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Feedback textarea */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes for Stratel <span className="text-gray-400 font-normal">(required when requesting changes)</span>
                    </label>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Describe any changes you need, questions you have, or let Stratel know you're happy with this version..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      rows={4}
                    />
                    {feedbackError && (
                      <p className="text-sm text-red-600 mt-1">{feedbackError}</p>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {mode === 'offline' && selectedFile ? (
                      <button
                        onClick={handleSubmitDraft}
                        disabled={isSubmitting}
                        className="sm:col-span-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {isSubmitting ? 'Submitting...' : 'Submit Edited File + Notes'}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleRequestChanges}
                          disabled={isSubmitting}
                          className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <MessageSquare className="w-4 h-4" />
                          {isSubmitting ? 'Sending...' : 'Request Changes'}
                        </button>
                        <button
                          onClick={handleApproveDraft}
                          disabled={isSubmitting || hasApproved || hasClientEdits}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {hasApproved ? 'Approved ✓' : 'Approve Draft'}
                        </button>
                      </>
                    )}
                  </div>

                  {approveError && <p className="text-sm text-red-600">{approveError}</p>}
                  {hasClientEdits && !approveError && (
                    <p className="text-sm text-amber-700 flex items-start gap-1.5">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      You have unsaved edits in the document. Use <strong>Request Changes</strong> to send your edits and notes to Stratel, or save your draft first.
                    </p>
                  )}

                  {/* Switch to offline editing — subtle secondary action */}
                  {isInAppOverlay && (
                    <p className="text-xs text-gray-400 text-center pt-1">
                      Prefer to edit in Word or another tool?{' '}
                      <button type="button" onClick={() => handleModeChange(mode === 'offline' ? 'in_app' : 'offline')} className="underline hover:text-gray-600">
                        {mode === 'offline' ? 'Switch back to in-browser editing' : 'Switch to offline editing'}
                      </button>
                    </p>
                  )}
                </div>
              ) : isMidRouting ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium">Submitting to Stratel is not available at this step.</p>
                  <p className="text-sm text-amber-700 mt-1">Use the routing controls above to pass this draft along your team's review sequence. The final reviewer will submit your team's response.</p>
                </div>
              ) : isDocumentSharedWithClient && document.status !== 'draft_approved' ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Your feedback has been submitted. Stratel is currently reviewing your response.</p>
                </div>
              ) : null}

              {/* Draft Approved Message */}
              {document.status === 'draft_approved' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Draft Approved</p>
                    <p className="text-sm text-green-700 mt-1">
                      Both your team and the internal team have approved this draft. The internal team is now preparing the final version.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {!isInAppOverlay && activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">
                  Compare the current draft against prior versions and export a diff.
                </p>
                <button
                  type="button"
                  onClick={() => { setExportType('diff'); setShowExportModal(true); }}
                  disabled={isExporting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-300 text-gray-800 text-sm font-medium rounded-lg transition-colors"
                >
                  Export Diff
                </button>
              </div>

              {!history ? (
                <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-600">
                  Loading history...
                </div>
              ) : history.iterations.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-600">
                  No client history yet.
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {history.iterations.map((iteration) => (
                    <div
                      key={iteration.iterationNumber}
                      className="border-b border-gray-200 last:border-b-0"
                    >
                      <button
                        onClick={() =>
                          setExpandedIteration(
                            expandedIteration === iteration.iterationNumber
                              ? null
                              : iteration.iterationNumber
                          )
                        }
                        className="w-full px-6 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                            {iteration.iterationNumber}
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">
                              {iteration.fileName}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Round {iteration.roundNumber} - {iteration.uploadedByRole} -{' '}
                              {new Date(iteration.uploadedAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-400 transition-transform ${
                            expandedIteration === iteration.iterationNumber ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {expandedIteration === iteration.iterationNumber && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 space-y-3">
                          <a
                            href={resolveFileUrl(iteration.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                          {iteration.commentCount > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                {iteration.commentCount} comment
                                {iteration.commentCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ExportFormatModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onSelect={handleExportSelect}
        title={exportType === 'download' ? 'Download Draft Format' : 'Export Diff Format'}
        isLoading={isExporting}
      />

      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Draft History & Diff</h3>
                <p className="text-sm text-gray-600">
                  Compare snapshots saved during the draft workflow.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyLoading ? (
                <p className="text-sm text-gray-500">Loading history...</p>
              ) : historyError ? (
                <p className="text-sm text-red-600">{historyError}</p>
              ) : (
                <VersionComparison versions={historyVersions} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isInAppOverlay) {
    return (
      <div className="fixed inset-0 bg-black/50 flex z-50">
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          <div className="border-b border-gray-200 px-3 py-2 sm:p-4 flex items-center justify-between gap-3 bg-gradient-to-r from-teal-50 to-teal-100">
            {headerLeft}
            <div className="flex items-center gap-3 flex-shrink-0">
              {headerRight}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-teal-50"
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-gray-50 p-3 sm:p-6">
            {body}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 rounded-lg client-draft-editor">
      <div className="flex items-center justify-between">
        {headerLeft}
        {headerRight}
      </div>
      {body}
    </div>
  );
}
